// frontend/src/components/VerifyEmail.jsx
import React, { useEffect, useRef, useState } from 'react';

import { API_BASE } from '../utils/apiBase';

export default function VerifyEmail({ initialEmail = '', onBackToLogin, onVerified }) {
  const [email, setEmail] = useState(initialEmail || '');
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [alreadyVerified, setAlreadyVerified] = useState(false);

  useEffect(() => {
    if (initialEmail) setEmail(initialEmail);
  }, [initialEmail]);

  const redirectTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  function redirectToLogin(verifiedEmail) {
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
    }
    redirectTimerRef.current = setTimeout(() => {
      if (typeof onVerified === 'function') {
        onVerified(verifiedEmail);
      } else if (typeof onBackToLogin === 'function') {
        onBackToLogin();
      }
    }, 2000);
  }

  // Probe the server to see if the email is already verified.
  async function probeAlreadyVerified(emailLower) {
    try {
      const res = await fetch(`${API_BASE}/auth/verify/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Any dummy code is fine; backend should short-circuit for active users.
        body: JSON.stringify({ email: emailLower, code: '__probe__' }),
      });
      const data = await res.json().catch(() => ({}));

      const okMsg = String(data?.message || '').toLowerCase();
      const errMsg = String(data?.detail || '').toLowerCase();

      // Accept a variety of signals from different backends.
      if (
        okMsg.includes('already verified') ||
        errMsg.includes('already verified') ||
        data?.alreadyVerified === true ||
        data?.verified === true ||
        data?.is_active === true
      ) {
        return true;
      }
    } catch {
      // Ignore probe errors; they just mean we couldn't confirm.
    }
    return false;
  }

  async function sendCode() {
    setErr('');
    setMsg('');
    if (!email) {
      setErr('Please enter your email.');
      return;
    }

    const emailLower = email.trim().toLowerCase();

    try {
      setPending(true);

      const res = await fetch(`${API_BASE}/auth/verify/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailLower }),
      });

      // Handle rate limiting explicitly
      if (res.status === 429) {
        const data = await res.json().catch(() => ({}));
        setErr(data?.detail || 'Too many requests. Please wait a minute and try again.');
        return;
      }

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.detail || 'Failed to send code');
      }

      // After a successful "send", check whether the email is already verified.
      const isVerified = await probeAlreadyVerified(emailLower);
      if (isVerified) {
        setAlreadyVerified(true);
        setMsg('Email address is already verified.');
      } else {
        const m = String(data?.message || '').toLowerCase();
        if (
          m.includes('already verified') ||
          data?.alreadyVerified === true ||
          data?.verified === true ||
          data?.is_active === true
        ) {
          setAlreadyVerified(true);
          setMsg('Email address is already verified.');
        } else {
          setMsg('Code sent. Check your email.');
        }
      }
    } catch (e) {
      setErr(e.message || 'Failed to send code');
    } finally {
      setPending(false);
    }
  }

  async function handleVerify(e) {
    e.preventDefault();
    setErr('');
    setMsg('');
    if (!email || !code) {
      setErr('Enter your email and 6-digit code.');
      return;
    }

    try {
      setPending(true);
      const res = await fetch(`${API_BASE}/auth/verify/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          code: code.trim(),
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.detail || 'Verification failed');
      }

      const verifiedEmail = email.trim().toLowerCase();
      setAlreadyVerified(true);
      setMsg('Verified! You can log in now. Redirecting to the login page...');
      redirectToLogin(verifiedEmail);
    } catch (e) {
      setErr(e.message || 'Verification failed');
    } finally {
      setPending(false);
    }
  }

  return (
    <form onSubmit={handleVerify} className="p-4 space-y-4 max-w-sm mx-auto">
      <h2 className="text-lg font-semibold">Verify your email</h2>

      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email"
        className="w-full border p-2 rounded"
        required
      />

      <div className="flex gap-2">
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="6-digit code"
          className="flex-1 border p-2 rounded"
          disabled={alreadyVerified}
        />
        <button
          type="button"
          onClick={sendCode}
          disabled={pending || !email}
          className="px-3 py-2 border rounded-xl disabled:opacity-60"
        >
          {pending ? "Sending..." : "Send code"}
        </button>
      </div>

      {err && <div className="text-red-600 dark:text-red-400">{err}</div>}
      {msg && <div className="text-green-700 dark:text-green-300">{msg}</div>}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => {
            if (onBackToLogin) return onBackToLogin();
            if (window.history.length > 1) return window.history.back();
            window.location.assign("/login");
          }}
          className="px-3 py-2 border rounded-xl inline-flex items-center justify-center gap-2"
          title="Back to login"
          aria-label="Back to login"
        >
          <i className="fi fi-rr-angle-left text-lg leading-none not-italic" />
        </button>

        <button
          type="submit"
          disabled={pending || !email || !code || alreadyVerified}
          className="bg-blue-600 text-white px-4 py-2 rounded-xl disabled:opacity-60"
        >
          {pending ? 'Verifying…' : 'Verify'}
        </button>
      </div>
    </form>
  );
}
